// File: src/components/management/UserAvailability/WeeklyAvailability.jsx

import React, { useEffect, useState } from "react";

const API_BASE = process.env.REACT_APP_API_BASE;

// Weekday labels aligned with MySQL weekday: 0 = Monday, ..., 6 = Sunday
const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function WeeklyAvailability({
  allUsers,
  weeklySettings,
  setWeeklySettings,
  handleSaveWeekly,
  loadingWeekly,
  handleStarClick,
}) {
  const [hasNormalised, setHasNormalised] = useState(false);

  const groupedByCompany = {};
  allUsers.forEach((u) => {
    const comp = u.COMPANY_NAME || "No Company";
    if (!groupedByCompany[comp]) groupedByCompany[comp] = [];
    groupedByCompany[comp].push(u);
  });

  useEffect(() => {
    if (hasNormalised || Object.keys(weeklySettings).length === 0) return;

    const normalised = {};
    for (const [userId, userCfg] of Object.entries(weeklySettings)) {
      const newDays = {};
      const newPriority = {};
      for (let i = 0; i < 7; i++) {
        const iStr = i.toString();
        newDays[i] = !!userCfg.days?.[i] || !!userCfg.days?.[iStr];
        newPriority[i] = parseInt(userCfg.priority?.[i] || userCfg.priority?.[iStr] || 0);
      }
      normalised[userId] = {
        days: newDays,
        priority: newPriority,
        maxConsec: userCfg.maxConsec || 6,
      };
    }

    setWeeklySettings(normalised);
    setHasNormalised(true);
  }, [weeklySettings, setWeeklySettings, hasNormalised]);

  const prepareAvailDays = () => {
    const out = [];
    for (const [userId, cfg] of Object.entries(weeklySettings)) {
      for (let wd = 0; wd < 7; wd++) {
        out.push({
          user_id: parseInt(userId),
          weekday: wd,
          is_enabled: cfg.days?.[wd] ? 1 : 0,
          priority: cfg.priority?.[wd] || 0,
        });
      }
    }
    return out;
  };

  const prepareMaxConsec = () => {
    return Object.entries(weeklySettings).map(([userId, cfg]) => ({
      user_id: parseInt(userId),
      max_consecutive_days: cfg.maxConsec || 6,
    }));
  };

  const handleSaveClick = async () => {
    const payload = {
      avail_days: prepareAvailDays(),
      max_consec: prepareMaxConsec(),
    };

    try {
      const res = await fetch(`${API_BASE}/app/API/rbmgt/set_user_availabilities.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "API error");

      alert("Availability saved successfully");
    } catch (err) {
      console.error("[ERROR] Saving availability failed:", err);
      alert("Failed to save. See console for details.");
    }
  };

  return (
    <section className="ua-weekly-section">
      <h2>Weekly Availability & Priority</h2>
      {loadingWeekly && <div className="ua-loading">Loading…</div>}

      <div className="ua-weekly-table-wrapper">
        <table className="ua-weekly-table">
          <thead>
            <tr>
              <th>User</th>
              {WEEKDAY_LABELS.map((lbl, i) => (
                <th key={i}><div className="wd-label">{lbl}</div></th>
              ))}
              <th>Max Consec Days</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(groupedByCompany).map(([companyName, usersInCompany]) => (
              <React.Fragment key={companyName}>
                <tr className="ua-company-row">
                  <td colSpan={WEEKDAY_LABELS.length + 2}>{companyName}</td>
                </tr>
                {usersInCompany.map((u) => {
                  const cfg = weeklySettings[u.USER_ID] || { days: {}, priority: {}, maxConsec: 6 };

                  return (
                    <tr key={u.USER_ID}>
                      <td className="ua-user-cell">
                        {u.USER_PROFILE_PIC_URL && (
                          <img src={u.USER_PROFILE_PIC_URL} alt="avatar" className="ua-avatar" />
                        )}
                        <div className="ua-user-info">
                          <span>{u.USER_NAME} {u.USER_SURNAME}</span>
                          <span className="ua-role-note">{u.ROLE_NAME}</span>
                        </div>
                      </td>
                      {WEEKDAY_LABELS.map((_, wd) => (
                        <td key={wd}>
                          <label className="ua-checkbox-wrapper">
                            <input
                              type="checkbox"
                              checked={!!cfg.days[wd]}
                              onChange={(e) => {
                                setWeeklySettings((prev) => {
                                  const copy = { ...prev };
                                  const userCfg = { ...copy[u.USER_ID] };
                                  userCfg.days = { ...userCfg.days, [wd]: e.target.checked };
                                  if (!e.target.checked) userCfg.priority[wd] = 0;
                                  copy[u.USER_ID] = userCfg;
                                  return copy;
                                });
                              }}
                            />
                            <span className="ua-checkbox-custom" />
                          </label>
                          {cfg.days?.[wd] && (
                            <span
                              className="ua-star-container"
                              onClick={() => handleStarClick(u.USER_ID, wd)}
                            >
                              <svg width="16" height="16" viewBox="0 0 576 512" fill="currentColor">
                                <path d="M259.3 17.8L194 150.2 47.9 171.5c-26.2 3.8-36.7 36.1-17.7 54.6l105.7 103-25 145.5c-4.5 26.2 23.2 46 46.4 33.7L288 439.6l130.7 68.7c23.2 12.2 50.8-7.4 46.4-33.7l-25-145.5 105.7-103c19-18.5 8.5-50.8-17.7-54.6L382 150.2 316.7 17.8c-11.7-23.6-45.6-23.9-57.4 0z" />
                              </svg>
                              <span className="ua-star-count">{cfg.priority?.[wd] || 0}</span>
                            </span>
                          )}
                        </td>
                      ))}
                      <td>
                        <input
                          type="number"
                          min="1"
                          max="14"
                          value={cfg.maxConsec}
                          onChange={(e) => {
                            let v = Math.max(1, Math.min(14, parseInt(e.target.value) || 1));
                            setWeeklySettings((prev) => {
                              const copy = { ...prev };
                              const userCfg = { ...copy[u.USER_ID], maxConsec: v };
                              copy[u.USER_ID] = userCfg;
                              return copy;
                            });
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="ua-actions">
        <button className="ua-btn ua-btn-save-weekly" onClick={handleSaveClick} disabled={loadingWeekly}>
          {loadingWeekly ? "Saving…" : "Save Weekly Settings"}
        </button>
      </div>
    </section>
  );
}
